/**
 * Pure preorder logic — option resolution, quantity rules, copy formatting
 * and cart line building. No DOM access; everything here is unit-testable in
 * a node environment and mirrors the theme app embed
 * (assets/js/preorder.js + assets/components/PreorderModal.js).
 */
import type {
  CartLine,
  SellingPlan,
  SellingPlanPaymentOption,
  StoqSettings,
  VariantState,
} from '../types'
import { normalizeLocale } from '../util'
import type { PreorderButtonContent, PreorderModalTexts, PreorderPaymentOption } from './types'

/** A record that may carry translated copy (a plan or one of its options). */
type Translatable = Record<string, unknown> & {
  translations?: Record<string, Record<string, unknown>> | null
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/** `"Translation missing: ..."` strings come through for unconfigured locales. */
function isValidTranslation(value: unknown): boolean {
  if (typeof value !== 'string') return value !== null && value !== undefined
  return !value.toLowerCase().startsWith('translation missing:')
}

/**
 * Translated value of a selling plan (or payment option) field. Mirrors the
 * embed's getTranslatedSellingPlanValue: translations apply only when the
 * shop has multi-language enabled and the requested locale differs from the
 * shop default; invalid/blank translations fall back to the default value.
 */
export function translatedPlanValue(
  record: Translatable,
  field: string,
  settings: Readonly<StoqSettings>,
  locale: string | undefined,
): string | null {
  const userLanguage = normalizeLocale(locale) ?? 'en'
  const defaultLocale = normalizeLocale(
    typeof settings.default_locale === 'string' ? settings.default_locale : undefined,
  )
  const multiLanguageEnabled = settings.multi_language_enabled === true

  const fallback = (): string | null => {
    const value = record[field]
    if (typeof value === 'string' && isValidTranslation(value)) return value
    return null
  }

  if (!multiLanguageEnabled || defaultLocale === userLanguage) {
    const value = record[field]
    return typeof value === 'string' ? value : null
  }

  const translations = record.translations ?? {}
  const localeTranslations = translations[userLanguage] ?? {}
  if (Object.prototype.hasOwnProperty.call(localeTranslations, field)) {
    const translated = localeTranslations[field]
    if (
      typeof translated === 'string'
      && translated !== ''
      && isValidTranslation(translated)
    ) {
      return translated
    }
  }

  return fallback()
}

/** ES2019-safe String#replaceAll. */
function replaceAll(text: string, token: string, value: string): string {
  return text.split(token).join(value)
}

/** The embed's parseTemplate: `{{ key }}` placeholders, unknown keys kept. */
export function parseTemplate(
  template: string,
  data: Record<string, string | number | null | undefined>,
): string {
  return template.replace(/{{\s*(\w+)\s*}}/g, (match, key: string) => {
    const value = data[key]
    return value !== undefined && value !== null ? String(value) : match
  })
}

/** Our locales are normalized with underscores; Intl wants BCP 47 hyphens. */
function intlLocale(locale: string | undefined): string {
  if (!locale) return 'en-GB'
  return locale.replace(/_/g, '-')
}

/**
 * Formats a money amount in the shop's base currency. Simplification vs the
 * embed: no presentment-currency conversion (`Shopify.currency.rate` only
 * exists inside the theme) and no `preorder_price_format` templating —
 * matches PreorderModal.js's own Intl-based formatter at rate 1.
 */
export function formatAmount(
  amount: number,
  settings: Readonly<StoqSettings>,
  locale: string | undefined,
): string {
  const currency = typeof settings.currency === 'string' && settings.currency !== ''
    ? settings.currency
    : 'USD'
  try {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    }).format(amount)
  } catch {
    return `${amount} ${currency}`
  }
}

/** `"50%"` or a formatted amount — the option's upfront charge. */
export function paymentValue(
  option: SellingPlanPaymentOption,
  settings: Readonly<StoqSettings>,
  locale: string | undefined,
): string | null {
  if (option.billing_checkout_charge_type === 'percentage') {
    const pct = toNumberOrNull(option.billing_checkout_charge_percentage)
    return pct !== null ? `${Math.trunc(pct)}%` : null
  }
  const amount = toNumberOrNull(option.billing_checkout_charge_amount)
  return amount !== null ? formatAmount(amount, settings, locale) : null
}

/** `"10%"` or a formatted amount — the option's discount. */
export function discountValue(
  option: SellingPlanPaymentOption,
  settings: Readonly<StoqSettings>,
  locale: string | undefined,
): string | null {
  if (option.pricing_type === 'percentage') {
    const pct = toNumberOrNull(option.pricing_percentage)
    return pct !== null ? `${Math.trunc(pct)}%` : null
  }
  const amount = toNumberOrNull(option.pricing_amount)
  return amount !== null ? formatAmount(amount, settings, locale) : null
}

/**
 * The plan's raw payment options sorted default-first (same comparator as
 * the embed). When the payload has no `payment_options` array (very old
 * cached plans) a single option is synthesized from the plan's own billing
 * columns — the embed does the same (`payment_options[0] || sellingPlan`).
 */
export function rawPaymentOptions(plan: SellingPlan): SellingPlanPaymentOption[] {
  const options = Array.isArray(plan.payment_options) && plan.payment_options.length > 0
    ? [...plan.payment_options]
    : [{
      shopify_selling_plan_id:
        plan.default_shopify_selling_plan_id ?? plan.shopify_selling_plan_id,
      is_default: true,
      billing_type: (plan['billing_type'] as string | null | undefined) ?? null,
      billing_checkout_charge_type:
        (plan['billing_checkout_charge_type'] as string | null | undefined) ?? null,
      billing_checkout_charge_amount:
        (plan['billing_checkout_charge_amount'] as number | string | null | undefined) ?? null,
      billing_checkout_charge_percentage:
        (plan['billing_checkout_charge_percentage'] as number | string | null | undefined) ?? null,
      pricing_type: (plan['pricing_type'] as string | null | undefined) ?? null,
      pricing_amount: (plan['pricing_amount'] as number | string | null | undefined) ?? null,
      pricing_percentage:
        (plan['pricing_percentage'] as number | string | null | undefined) ?? null,
      billing_title: (plan['billing_title'] as string | null | undefined) ?? null,
      billing_description: (plan['billing_description'] as string | null | undefined) ?? null,
      discount_text: (plan['discount_text'] as string | null | undefined) ?? null,
      translations: plan.translations ?? null,
    } satisfies SellingPlanPaymentOption]

  const defaultId = toNumberOrNull(plan.default_shopify_selling_plan_id)
  options.sort((a, b) => {
    if (defaultId !== null) {
      if (toNumberOrNull(a.shopify_selling_plan_id) === defaultId) return -1
      if (toNumberOrNull(b.shopify_selling_plan_id) === defaultId) return 1
    }
    if (a.is_default === true && b.is_default !== true) return -1
    if (a.is_default !== true && b.is_default === true) return 1
    return 0
  })
  return options
}

/**
 * Payment options resolved for display: translated copy, placeholders
 * substituted, default option first (and flagged `isDefault`).
 */
export function resolvePaymentOptions(
  plan: SellingPlan,
  settings: Readonly<StoqSettings>,
  locale: string | undefined,
): PreorderPaymentOption[] {
  return rawPaymentOptions(plan).map((option, index) => {
    const sellingPlanId = toNumberOrNull(option.shopify_selling_plan_id)

    const rawDescription = translatedPlanValue(option, 'billing_description', settings, locale)
    const description = rawDescription !== null && rawDescription.trim() !== ''
      ? parseTemplate(rawDescription, { payment: paymentValue(option, settings, locale) })
      : null

    let discountLabel: string | null = null
    if (
      typeof option.pricing_type === 'string'
      && option.pricing_type !== ''
      && option.pricing_type !== 'no_discount'
    ) {
      const rawDiscountText = translatedPlanValue(option, 'discount_text', settings, locale)
      if (rawDiscountText !== null && rawDiscountText.trim() !== '') {
        discountLabel = parseTemplate(rawDiscountText, {
          discount: discountValue(option, settings, locale),
        })
      }
    }

    const type = option.type === 'full' || option.type === 'partial'
      ? option.type
      : (option.billing_type === 'no_remaining_balance' ? 'full' : 'partial')

    return {
      sellingPlanId,
      sellingPlanGid: sellingPlanId !== null ? `gid://shopify/SellingPlan/${sellingPlanId}` : null,
      isDefault: index === 0,
      type,
      title: translatedPlanValue(option, 'billing_title', settings, locale) ?? '',
      description,
      discountLabel,
      raw: option,
    }
  })
}

/**
 * Whether the modal UI is required, mirroring the embed's shouldShowModal:
 * multiple payment options to choose between, or a required acknowledgement
 * checkbox. (The embed also opens it for quantity validation errors —
 * openPreorderModal does the same at call time.)
 */
export function needsModal(plan: SellingPlan, options: PreorderPaymentOption[]): boolean {
  return options.length > 1 || plan.require_preorder_acknowledgement === true
}

export interface QuantityBounds {
  min: number
  /** null = unlimited (no per-order max and no remaining-unit limit). */
  max: number | null
}

/**
 * Quantity bounds for the stepper. `remainingCount` (limit − sold) caps the
 * max for STOQ-managed plans; per the embed, a minimum above the remaining
 * units relaxes to 1 so the last units stay sellable. The SDK has no cart
 * visibility, so cart-quantity adjustments from the embed are not applied.
 */
export function quantityBounds(plan: SellingPlan, state: VariantState): QuantityBounds {
  const minSetting = toNumberOrNull(plan.preorder_min_quantity)
  const maxSetting = toNumberOrNull(plan.preorder_max_quantity)
  const remaining = state.remainingCount

  let min = minSetting !== null && minSetting > 1 ? minSetting : 1
  if (remaining !== null && remaining < min) min = 1

  const caps = [
    maxSetting !== null && maxSetting > 0 ? maxSetting : null,
    remaining,
  ].filter((value): value is number => value !== null)
  const max = caps.length > 0 ? Math.min(...caps) : null

  return { min, max }
}

/** Clamps shopper input into the bounds (always at least `min`). */
export function clampQuantity(raw: unknown, bounds: QuantityBounds): number {
  const parsed = Math.floor(Number(raw))
  let qty = Number.isFinite(parsed) && parsed > 0 ? parsed : bounds.min
  qty = Math.max(bounds.min, qty)
  if (bounds.max !== null && bounds.max >= bounds.min) qty = Math.min(qty, bounds.max)
  return qty
}

/**
 * Validation message for a requested quantity, or null when valid. Copy and
 * precedence (remaining units → per-order max → per-order min) match the
 * embed's PreorderModal.
 */
export function validateQuantity(
  qty: number,
  plan: SellingPlan,
  state: VariantState,
): string | null {
  const remaining = state.remainingCount
  const minQty = toNumberOrNull(plan.preorder_min_quantity)
  const maxQty = toNumberOrNull(plan.preorder_max_quantity)

  if (remaining !== null && qty > remaining) {
    if (remaining > 0) {
      return `You can only preorder ${remaining} unit${remaining !== 1 ? 's' : ''}`
    }
    return 'No units available for preorder'
  }
  if (maxQty !== null && maxQty > 0 && qty > maxQty) {
    return `Maximum quantity is ${maxQty} unit${maxQty > 1 ? 's' : ''} per order`
  }
  if (
    minQty !== null && minQty > 0 && qty < minQty
    && (remaining === null || remaining >= minQty)
  ) {
    return `Minimum quantity is ${minQty} unit${minQty > 1 ? 's' : ''} per order`
  }
  return null
}

/**
 * Storefront Cart API line for a confirmed preorder, honoring the CHOSEN
 * option's selling plan id. Property-based plans
 * (`use_shopify_selling_plan: false`) intentionally never get a
 * `sellingPlanId`.
 */
export function buildPreorderLine(
  plan: SellingPlan,
  variantId: number,
  quantity: number,
  sellingPlanId: number | null,
): CartLine {
  const line: CartLine = {
    merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
    quantity,
  }
  if (sellingPlanId === null) return line

  if (plan.use_shopify_selling_plan === false) {
    // Property-based offer: STOQ does not attach a Shopify selling plan to
    // the line — it recognizes the preorder from these line-item properties
    // (the same two the theme app embed writes, see preorder.js). Without
    // them the order is a plain full-price sale as far as STOQ can tell.
    line.attributes = propertyBasedPreorderAttributes(sellingPlanId)
  } else {
    line.sellingPlanId = `gid://shopify/SellingPlan/${sellingPlanId}`
  }
  return line
}

/** Line-item properties that mark a preorder under a property-based offer. */
export function propertyBasedPreorderAttributes(sellingPlanId: number): CartLine['attributes'] {
  return [
    { key: '_stoq_shopify_selling_plan_id', value: String(sellingPlanId) },
    { key: 'Purchase type', value: 'Preorder' },
  ]
}

function formatDeliveryDate(
  date: Date,
  locale: string | undefined,
  parts: Intl.DateTimeFormatOptions,
): string {
  try {
    return new Intl.DateTimeFormat(intlLocale(locale), parts).format(date)
  } catch {
    return date.toDateString()
  }
}

type DateGranularity = 'date' | 'month_year' | 'year'

const DATE_PARTS: Record<DateGranularity, Intl.DateTimeFormatOptions> = {
  date: { year: 'numeric', month: 'short', day: 'numeric' },
  month_year: { year: 'numeric', month: 'short' },
  year: { year: 'numeric' },
}

/** V2 ("simplified") shipping variable values, per the embed. */
function v2DateValue(
  plan: SellingPlan,
  granularity: DateGranularity,
  locale: string | undefined,
): string {
  switch (plan.delivery_type) {
    case 'asap':
      return 'as soon as possible'
    case 'unknown':
      return 'to be confirmed'
    case 'anchor': {
      const days = toNumberOrNull(plan.delivery_after_n_intervals)
      if (days === null) return ''
      const target = new Date()
      target.setDate(target.getDate() + days)
      return formatDeliveryDate(target, locale, DATE_PARTS[granularity])
    }
    case 'exact_time': {
      if (!plan.delivery_at) return ''
      const target = new Date(plan.delivery_at)
      if (Number.isNaN(target.getTime())) return ''
      return formatDeliveryDate(target, locale, DATE_PARTS[granularity])
    }
    default:
      return ''
  }
}

/**
 * The plan's `preorder_shipping_text` with its variables substituted —
 * the embed's getFormattedShippingText. V1 plans replace `{{ date }}` with
 * the plan-wide `shipping_text` or the per-variant shipping text; V2
 * ("simplified") all-products plans compute `{{ days }}` / `{{ date }}` /
 * `{{ month_year }}` / `{{ year }}` from the delivery settings. Returns null
 * when the plan has no template or no date value is available.
 */
export function formattedShippingText(
  plan: SellingPlan,
  variantShippingText: string | null,
  settings: Readonly<StoqSettings>,
  locale: string | undefined,
): string | null {
  const template = translatedPlanValue(plan, 'preorder_shipping_text', settings, locale)
  if (template === null || template === '') return null

  if (plan.shipping_applies_to_all_products === true) {
    let text = template
    const days = toNumberOrNull(plan.delivery_after_n_intervals)
    if (plan.delivery_type === 'anchor' && days !== null) {
      text = replaceAll(text, '{{ days }}', String(days))
    }
    for (const granularity of ['date', 'month_year', 'year'] as const) {
      const value = v2DateValue(plan, granularity, locale)
      if (value !== '') text = replaceAll(text, `{{ ${granularity} }}`, value)
    }
    return text
  }
  return variantShippingText !== null && variantShippingText !== ''
    ? replaceAll(template, '{{ date }}', variantShippingText)
    : null
}

/**
 * The plan's `quantity_limit_text` with `{{ quantity }}` (remaining),
 * `{{ sold }}` and `{{ total }}` substituted. Null when the merchant hides
 * the limit, no limit is configured, or there is no template.
 */
export function quantityLimitText(
  plan: SellingPlan,
  state: VariantState,
  settings: Readonly<StoqSettings>,
  locale: string | undefined,
): string | null {
  if (plan.preorder_button_description_show_quantity_limit !== true) return null
  if (state.remainingCount === null || state.maxCount === null) return null
  const template = translatedPlanValue(plan, 'quantity_limit_text', settings, locale)
  if (template === null || template === '') return null
  return parseTemplate(template, {
    quantity: state.remainingCount,
    sold: state.maxCount - state.remainingCount,
    total: state.maxCount,
  })
}

/** Merchant-configured CTA copy. See {@link StoqClient.preorderButtonFor}. */
export function buildButtonContent(
  plan: SellingPlan,
  state: VariantState,
  settings: Readonly<StoqSettings>,
  locale: string | undefined,
): PreorderButtonContent {
  const label = translatedPlanValue(plan, 'preorder_button_text', settings, locale)
  const disclaimer = plan.preorder_button_description_enabled !== false
    ? translatedPlanValue(plan, 'preorder_button_description', settings, locale)
    : null
  return {
    label: label !== null && label !== '' ? label : 'Preorder',
    disclaimer: disclaimer !== null && disclaimer.trim() !== '' ? disclaimer : null,
    shippingText: formattedShippingText(plan, state.shippingText, settings, locale),
  }
}

function stringSetting(settings: Readonly<StoqSettings>, key: string): string | undefined {
  const value = settings[key]
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

export const DEFAULT_PREORDER_TEXTS: Omit<PreorderModalTexts, 'title'> = {
  paymentHeading: 'Payment',
  continueLabel: 'Continue',
  cancelLabel: 'Cancel',
  closeLabel: 'Close modal',
  quantityLabel: 'Quantity',
  decreaseLabel: 'Decrease quantity',
  increaseLabel: 'Increase quantity',
}

/**
 * Resolves the modal texts: per-call override → shop setting/plan copy →
 * built-in default. The title defaults to the plan's translated
 * `preorder_button_text`, same as the embed's modal heading.
 */
export function resolvePreorderTexts(
  plan: SellingPlan,
  settings: Readonly<StoqSettings>,
  locale: string | undefined,
  overrides?: Partial<PreorderModalTexts>,
): PreorderModalTexts {
  const planTitle = translatedPlanValue(plan, 'preorder_button_text', settings, locale)
  return {
    title: overrides?.title
      ?? (planTitle !== null && planTitle !== '' ? planTitle : 'Preorder'),
    paymentHeading: overrides?.paymentHeading ?? DEFAULT_PREORDER_TEXTS.paymentHeading,
    continueLabel: overrides?.continueLabel
      ?? stringSetting(settings, 'preorder_modal_continue_button_text')
      ?? DEFAULT_PREORDER_TEXTS.continueLabel,
    cancelLabel: overrides?.cancelLabel
      ?? stringSetting(settings, 'preorder_modal_cancel_button_text')
      ?? DEFAULT_PREORDER_TEXTS.cancelLabel,
    closeLabel: overrides?.closeLabel ?? DEFAULT_PREORDER_TEXTS.closeLabel,
    quantityLabel: overrides?.quantityLabel ?? DEFAULT_PREORDER_TEXTS.quantityLabel,
    decreaseLabel: overrides?.decreaseLabel ?? DEFAULT_PREORDER_TEXTS.decreaseLabel,
    increaseLabel: overrides?.increaseLabel ?? DEFAULT_PREORDER_TEXTS.increaseLabel,
  }
}

/** The plan's acknowledgement checkbox copy (HTML), with the embed's fallback. */
export function acknowledgementText(
  plan: SellingPlan,
  settings: Readonly<StoqSettings>,
  locale: string | undefined,
): string {
  const text = translatedPlanValue(plan, 'preorder_acknowledgement_text', settings, locale)
  return text !== null && text.trim() !== ''
    ? text
    : 'I acknowledge and agree to the preorder terms and conditions for this product.'
}
